import { Injectable } from '@angular/core';
import { VesselStateResponse } from '@core/api/state-api.service';
import { DateNow } from '@core/constants/time.defaults';
import { VesselsStateSocketsService } from '@core/services/sockets/vessels-state-sockets.service';
import { StoreInitService } from '@dashboard/dashboard-library/helpers/store-init.service';
import {
  DashboardBaseButtonsState,
  DashboardOverlayButtonsState,
  RealtimeIsActiveSelector,
  RightBarViewSelector,
  SelectedBaseMapLayers,
  SelectedOverlayMapLayers,
  SelectedVesselSelector,
  SetVariablesDataAction,
  ToggleDmsAction,
  ToggleVesselNameInfoAction,
  ToggleWindyLayerAction,
  ToolsLayerSelector,
  VesselsSelector,
  WindyObjectLayerSelector,
  MapZoomSelector,
  WindyLayerSelector,
} from '@dashboard/dashboard-store';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subscription,
  debounceTime,
  map,
  tap,
  of,
  switchMap,
  firstValueFrom,
  from,
  delay,
} from 'rxjs';
import { ExtendButton } from '../extend-more-buttons/extend-more-butons.types';
import { NzModalService } from 'ng-zorro-antd/modal';
import { WeatherConfigComponent } from '@widgets/map-widget/components/weather-config/weather-config.component';
import { WeatherDataService } from '@widgets/map-widget/services/weather-data.service';

@Injectable()
export class DynamicDashboardWrapperHelperService {
  private wsSubscription: Subscription;

  public zoom$ = this._store.select(MapZoomSelector);

  public vessels$ = this._store.select(VesselsSelector);

  public activeVessel$ = this._store.select(SelectedVesselSelector);

  public selectedLayerIds$ = this._store.select(SelectedOverlayMapLayers);

  public rightBar$ = this._store.select(RightBarViewSelector);

  public selectedBaseLayersIds$ = this._store
    .select(SelectedBaseMapLayers)
    .pipe(map((d) => [d]));

  public selectedBaseLayerId$ = this._store.select(SelectedBaseMapLayers);

  public _overlayLayers$ = this._store.select(DashboardOverlayButtonsState);

  public baseLayers$ = this._store.select(DashboardBaseButtonsState);

  public overlayLayers$ = this._store.select(SelectedBaseMapLayers).pipe(
    switchMap((baseMapId) =>
      this._overlayLayers$.pipe(
        map((layerButtons) => {
          // Determine if the selected base layer ID is non-zero
          const isBaseLayerNonZero = baseMapId !== '0';

          // Update the `disabled` property for each layer
          const updatedLayers = layerButtons.layers.map((layer) => ({
            ...layer,
            disabled: isBaseLayerNonZero || layer.disabled, // Keep existing `disabled` state if already true
            disabledTooltip:
              isBaseLayerNonZero && !layer.disabled
                ? 'Select the Konnecta GeoServer base map to enable this layer'
                : layer.disabledTooltip,
          }));

          // Construct the updated overlay layers state
          return {
            ...layerButtons,
            layers: updatedLayers,
          };
        })
      )
    )
  );

  public WindLayers$: Observable<ExtendButton[]> = this._store
    .select(WindyObjectLayerSelector)
    .pipe(
      switchMap((a) => {
        const { windyType, loading } = a;
        return of([
          {
            id: 'w1',
            tooltip: 'Wind',
            icon: 'assets/icons/dashboard/wind.svg',
            selected: windyType === 'wind',
            loading,
            disabled: loading,
            action: async () => {
              this._store.dispatch(
                ToggleWindyLayerAction({
                  windyType: windyType === 'wind' ? null : 'wind',
                })
              );
            },
          },
          {
            id: 'w2',
            tooltip: 'Waves',
            icon: 'assets/icons/dashboard/wave-ocean-sea.svg',
            selected: windyType === 'wave',
            loading,
            disabled: loading,
            action: async () => {
              this._store.dispatch(
                ToggleWindyLayerAction({
                  windyType: windyType === 'wave' ? null : 'wave',
                })
              );
            },
          },
          {
            id: 'w3',
            tooltip: 'Currents',
            icon: 'assets/icons/dashboard/waves2.svg',
            selected: windyType === 'currents',
            loading,
            disabled: loading,
            action: async () => {
              this._store.dispatch(
                ToggleWindyLayerAction({
                  windyType: windyType === 'currents' ? null : 'currents',
                })
              );
            },
          },
          {
            id: 'w4',
            tooltip: 'Config',
            icon: '',
            action: async () => {
              this.openConfigDialog();
            },
          },
        ]);
      })
    );

  public VesselInfoLayers$: Observable<ExtendButton[]> = this._store
    .select(ToolsLayerSelector)
    .pipe(
      switchMap(({ vesselNameLayer, dmsTool }) =>
        of([
          {
            id: 'v1',
            tooltip: 'Show/Hide Vessel Names',
            icon: 'assets/icons/dashboard/tooltip.svg',
            selected: vesselNameLayer,
            action: () => {
              this._store.dispatch(ToggleVesselNameInfoAction());
            },
          },
          {
            id: 'v2',
            tooltip: 'Toggle Measurement Tool',
            icon: 'assets/icons/dashboard/measure.svg',
            selected: dmsTool,
            action: () => {
              this._store.dispatch(ToggleDmsAction());
            },
          },
        ])
      )
    );

  constructor(
    private _store: Store,
    private _vesselStateWS: VesselsStateSocketsService,
    private _storeInitHelper: StoreInitService,
    private modal: NzModalService,
    private weatherSrv: WeatherDataService
  ) {}

  async openConfigDialog() {
    const activeLayer = await firstValueFrom(
      this._store.select(WindyLayerSelector)
    );
    return new Promise((resolve) => {
      const modal = this.modal.create({
        nzTitle: `Weather Visualization Configuration`,
        nzContent: WeatherConfigComponent,
        nzWidth: '600px',
        nzCancelText: null,
        nzOkText: null,
        nzFooter: null,
      });
      modal.afterClose.subscribe(async (data) => {
        if (data) {
          console.log('WEATHER:', data);
          const weatherType = data.weatherType;
          this.weatherSrv.DefaultWeatherConfig[weatherType] = data;
          console.log({ ...this.weatherSrv.DefaultWeatherConfig });
          if (activeLayer === weatherType)
            this.weatherSrv.showWindyLayer(weatherType);
          resolve(data);
        }
        resolve(null);
      });
    });
  }

  private WSTestingListener() {
    // from([WSStates[0]])
    from([])
      .pipe(delay(7000))
      // .pipe(
      //   concatMap((stateArray, index) =>
      //     // Emit each array item with a delay of 10 seconds
      //     timer(index * 10000).pipe(concatMap(() => [stateArray]))
      //   )
      // )
      .subscribe((values) => {
        const variableMeta = values
          .map(this._storeInitHelper.VesselStateDataToActionData)
          .flat();
        this._store.dispatch(
          SetVariablesDataAction({ variableMeta, source: 'WS' })
        );
      });
  }
  public StartWSListener() {
    // this.WSTestingListener();

    this.wsIOTInit((vals: VesselStateResponse[]) => {
      const variableMeta = vals
        .map(this._storeInitHelper.VesselStateDataToActionData)
        .flat();
      this._store.dispatch(
        SetVariablesDataAction({ variableMeta, source: 'WS' })
      );
    });
  }
  public StopWsListener() {
    this.wsSubscription.unsubscribe();
  }
  private wsIOTInit(evalFunc: (vals: VesselStateResponse[]) => void) {
    let StateGrouping: VesselStateResponse[] = [];

    /**
     * Test the ws behavior with a mock object
     * const testVal = new IOTWsMessage({});
     * of(testVal)
        .pipe(delay(60000))
     */

    // this._vesselStateWS
    //   .createIotConnection()
    //   .subscribe((d) => console.log({ d }));
    this.wsSubscription = this._vesselStateWS
      .createIotConnection()
      .pipe(
        tap((stateData) => {
          StateGrouping.push(stateData);
        }),
        debounceTime(3000),
        switchMap((stateData) =>
          this._store.select(RealtimeIsActiveSelector).pipe(
            tap((activeRealTime) => {
              if (!activeRealTime || !stateData) return;

              evalFunc(StateGrouping);
              console.log('WS DATA', DateNow(), StateGrouping.length);
              StateGrouping = [];
            }),
            map(() => StateGrouping)
          )
        )
      )
      .subscribe();
  }
}
